import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { XIcon, ArrowCounterClockwiseIcon, GearIcon } from '@phosphor-icons/react'
import { PANEL_SIZES, POSITIONS } from '../../../../context/HelpBotContext'
import { ScoopText, ScoopRow, ScoopColumn, ScoopStatusIndicator } from '../../../ui'
import { HelpBotAIAvatar } from '../HelpBotAIAvatar/HelpBotAIAvatar'
import { HelpBotEmptyState } from '../HelpBotEmptyState/HelpBotEmptyState'
import { HelpBotMessage } from '../HelpBotMessage/HelpBotMessage'
import { HelpBotInput } from '../HelpBotInput/HelpBotInput'
import styles from './HelpBotPanel.module.scss'

const ANIMATION_DURATION = 180

export const HelpBotPanel = ({
    isOpen,
    messages,
    isPending,
    hasUnread,
    panelSize,
    position,
    onClose,
    onClearChat,
    onSendMessage,
    onMarkAsRead,
    onChangePanelSize,
    onChangePosition,
}) => {
    const [shouldRender, setShouldRender] = useState(isOpen)
    const [isVisible, setIsVisible] = useState(false)
    const [canAnimate, setCanAnimate] = useState(false)
    const [showSizeMenu, setShowSizeMenu] = useState(false)
    const sizeMenuRef = useRef(null)
    const messagesContainerRef = useRef(null)
    const lastMessageRef = useRef(null)
    const typingIndicatorRef = useRef(null)
    const prevMessageCountRef = useRef(0)
    const wasPendingRef = useRef(false)
    const wasOpenRef = useRef(isOpen)
    const hadUnreadRef = useRef(false)

    // Scroll helpers
    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'instant',
            })
        }
    }

    const scrollToElement = (element) => {
        if (element && messagesContainerRef.current) {
            const container = messagesContainerRef.current
            const elementTop = element.offsetTop - container.offsetTop
            container.scrollTo({
                top: elementTop,
                behavior: 'smooth',
            })
        }
    }

    // Handle panel open/close animation
    useEffect(() => {
        if (isOpen) {
            setShouldRender(true)
            const showTimer = requestAnimationFrame(() => {
                setCanAnimate(true)
                requestAnimationFrame(() => {
                    setIsVisible(true)
                })
            })
            return () => {
                cancelAnimationFrame(showTimer)
            }
        } else {
            setIsVisible(false)
            const hideTimer = setTimeout(() => {
                setShouldRender(false)
                setCanAnimate(false)
            }, ANIMATION_DURATION)
            return () => clearTimeout(hideTimer)
        }
    }, [isOpen])

    // Capture hasUnread state while panel is closed
    useEffect(() => {
        if (!isOpen) {
            hadUnreadRef.current = hasUnread
        }
    }, [isOpen, hasUnread])

    // Pre-scroll before panel becomes visible (prevents visible scroll jump)
    useLayoutEffect(() => {
        // Only run when panel is about to show (rendered but not yet visible)
        if (!shouldRender || isVisible || !messagesContainerRef.current || messages.length === 0) {
            return
        }

        // Set scroll position synchronously before paint
        if (hadUnreadRef.current && lastMessageRef.current) {
            const container = messagesContainerRef.current
            const elementTop = lastMessageRef.current.offsetTop - container.offsetTop
            container.scrollTop = elementTop
        } else {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
        }
    }, [shouldRender, isVisible, messages.length])

    // Track panel open state for other effects
    useEffect(() => {
        wasOpenRef.current = isOpen
    }, [isOpen])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sizeMenuRef.current && !sizeMenuRef.current.contains(event.target)) {
                setShowSizeMenu(false)
            }
        }
        if (showSizeMenu) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showSizeMenu])

    // Handle scroll when in chat (panel already open)
    useEffect(() => {
        // Only handle scroll if panel is open
        if (!isOpen) return

        const currentCount = messages.length
        const prevCount = prevMessageCountRef.current
        const wasPending = wasPendingRef.current

        // New message arrived
        if (currentCount > prevCount) {
            const lastMessage = messages[messages.length - 1]

            // Bot message arrived (was pending, now has response)
            if (lastMessage?.role === 'assistant' && wasPending) {
                // Small delay to ensure ref is set
                setTimeout(() => {
                    scrollToElement(lastMessageRef.current)
                }, 50)
            } else if (lastMessage?.role === 'user') {
                // User sent message -> scroll to bottom (typing indicator will appear)
                scrollToBottom()
            }
        }

        prevMessageCountRef.current = currentCount
    }, [messages, isOpen])

    // Handle scroll when bot starts/stops thinking
    useEffect(() => {
        if (!isOpen) {
            wasPendingRef.current = isPending
            return
        }

        // Bot started thinking -> scroll to typing indicator
        if (isPending && !wasPendingRef.current) {
            setTimeout(() => {
                scrollToElement(typingIndicatorRef.current)
            }, 50)
        }

        wasPendingRef.current = isPending
    }, [isPending, isOpen])

    const handleClearChat = () => {
        onClearChat?.()
    }

    const handleSizeChange = (size) => {
        onChangePanelSize?.(size)
    }

    const handlePositionChange = (pos) => {
        onChangePosition?.(pos)
    }

    if (!shouldRender) return null

    const panelClassName = [
        styles.panel,
        canAnimate && styles.animating,
        isVisible && styles.open,
        panelSize === PANEL_SIZES.LARGE && styles.large,
        panelSize === PANEL_SIZES.WIDE && styles.wide,
        position === POSITIONS.LEFT && styles.left,
    ]
        .filter(Boolean)
        .join(' ')

    const hasMessages = messages.length > 0

    return (
        <div className={panelClassName} role="dialog" aria-label="Help chat">
            <ScoopRow align="center" justify="space-between" className={styles.header}>
                <ScoopRow align="center" gap={8}>
                    <HelpBotAIAvatar status="idle" size={37} />
                    <ScoopColumn gap={2}>
                        <ScoopText className={styles.botName}>Scoop Assistant</ScoopText>
                        <ScoopRow align="center" gap={4}>
                            <ScoopStatusIndicator variant="success" />
                            <ScoopText className={styles.botSubtitle}>Ready to help you!</ScoopText>
                        </ScoopRow>
                    </ScoopColumn>
                </ScoopRow>
                <ScoopRow align="center" gap={4}>
                    {hasMessages && !isPending && (
                        <button
                            className={styles.headerButton}
                            onClick={handleClearChat}
                            aria-label="Clear chat"
                            title="Clear chat"
                        >
                            <ArrowCounterClockwiseIcon size={18} />
                        </button>
                    )}
                    <div className={styles.sizeMenuWrapper} ref={sizeMenuRef}>
                        <button
                            className={styles.headerButton}
                            onClick={() => setShowSizeMenu(!showSizeMenu)}
                            aria-label="Panel size"
                            title="Panel size"
                        >
                            <GearIcon size={18} />
                        </button>
                        {showSizeMenu && (
                            <div className={styles.sizeMenu}>
                                <ScoopText className={styles.sizeMenuTitle}>Panel Size</ScoopText>
                                <div className={styles.sizeOptions}>
                                    <button
                                        className={`${styles.sizeOption} ${panelSize === PANEL_SIZES.DEFAULT ? styles.active : ''}`}
                                        onClick={() => handleSizeChange(PANEL_SIZES.DEFAULT)}
                                        title="Default"
                                    >
                                        <div className={styles.sizePreview}>
                                            <div className={styles.sizePreviewInner} />
                                        </div>
                                    </button>
                                    <button
                                        className={`${styles.sizeOption} ${panelSize === PANEL_SIZES.LARGE ? styles.active : ''}`}
                                        onClick={() => handleSizeChange(PANEL_SIZES.LARGE)}
                                        title="Large"
                                    >
                                        <div className={`${styles.sizePreview} ${styles.large}`}>
                                            <div className={styles.sizePreviewInner} />
                                        </div>
                                    </button>
                                    <button
                                        className={`${styles.sizeOption} ${panelSize === PANEL_SIZES.WIDE ? styles.active : ''}`}
                                        onClick={() => handleSizeChange(PANEL_SIZES.WIDE)}
                                        title="Wide"
                                    >
                                        <div className={`${styles.sizePreview} ${styles.wide}`}>
                                            <div className={styles.sizePreviewInner} />
                                        </div>
                                    </button>
                                </div>
                                <div className={styles.settingsSection}>
                                    <ScoopText className={styles.sizeMenuTitle}>Position</ScoopText>
                                    <div className={styles.positionOptions}>
                                        <button
                                            className={`${styles.positionOption} ${position === POSITIONS.LEFT ? styles.active : ''}`}
                                            onClick={() => handlePositionChange(POSITIONS.LEFT)}
                                            title="Left"
                                        >
                                            <div
                                                className={`${styles.positionPreview} ${styles.left}`}
                                            >
                                                <div className={styles.positionDot} />
                                            </div>
                                        </button>
                                        <button
                                            className={`${styles.positionOption} ${position === POSITIONS.RIGHT ? styles.active : ''}`}
                                            onClick={() => handlePositionChange(POSITIONS.RIGHT)}
                                            title="Right"
                                        >
                                            <div
                                                className={`${styles.positionPreview} ${styles.right}`}
                                            >
                                                <div className={styles.positionDot} />
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <button className={styles.headerButton} onClick={onClose} aria-label="Close">
                        <XIcon size={18} />
                    </button>
                </ScoopRow>
            </ScoopRow>

            <div ref={messagesContainerRef} className={styles.messagesContainer}>
                {!hasMessages ? (
                    <HelpBotEmptyState onSuggestionClick={onSendMessage} />
                ) : (
                    <>
                        {messages.map((msg, index) => (
                            <div
                                key={msg.id}
                                ref={index === messages.length - 1 ? lastMessageRef : null}
                            >
                                <HelpBotMessage message={msg} />
                            </div>
                        ))}
                        {isPending && (
                            <div ref={typingIndicatorRef} className={styles.typingIndicator}>
                                <HelpBotAIAvatar size={37} />
                                <div className={styles.typingBubble}>
                                    <span className={styles.typingDot} />
                                    <span className={styles.typingDot} />
                                    <span className={styles.typingDot} />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <HelpBotInput onSend={onSendMessage} disabled={isPending} />
        </div>
    )
}
